import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, MapPin, Gift, X, ArrowRight } from "lucide-react";
import api from "@/lib/api";

export default function BookFlow() {
  const loc = useLocation();
  const [cities, setCities] = useState([]);
  const [pkgs, setPkgs] = useState([]);
  const [cityId, setCityId] = useState(loc.state?.city || new URLSearchParams(loc.search).get("city") || "");
  const [pkgKey, setPkgKey] = useState(loc.state?.pkg || new URLSearchParams(loc.search).get("pkg") || "");
  const [slot, setSlot] = useState("");
  const [notes, setNotes] = useState("");
  const [voucherCode, setVoucherCode] = useState("");
  const [voucher, setVoucher] = useState(null);
  const [checkingVoucher, setCheckingVoucher] = useState(false);
  const [paymentOption, setPaymentOption] = useState("full");
  const [submitting, setSubmitting] = useState(false);
  const [loadingCities, setLoadingCities] = useState(true);
  const [citiesError, setCitiesError] = useState(null);

  useEffect(() => {
    setLoadingCities(true);
    Promise.all([
      api.get("/cities").then(r => setCities(r.data)),
      api.get("/packages").then(r => setPkgs(r.data)),
    ]).catch(err => setCitiesError(err?.response?.data?.detail || "Could not load booking data. Please refresh."))
      .finally(() => setLoadingCities(false));
  }, []);

  const activeCity = cities.find(c => c.city_id === cityId);
  const activePkg = pkgs.find(p => p.lookup_key === pkgKey);
  const bookableCities = cities.filter(c => c.status === "now_booking" && (c.slots || []).length > 0);
  const nowUtc = Date.now();
  const futureSlots = (activeCity?.slots || []).filter(s => {
    const t = new Date(s).getTime();
    return !Number.isNaN(t) && t > nowUtc;
  });

  const submit = async () => {
    if (submitting) return;  // double-submit guard
    if (!cityId || !slot || !pkgKey) return toast.error("Pick a city, time, and experience.");
    if (new Date(slot).getTime() <= nowUtc) return toast.error("That session time has passed. Pick a future slot.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        city_id: cityId, slot_iso: slot, package_lookup_key: pkgKey, notes,
        origin_url: window.location.origin,
        voucher_code: voucher ? voucher.code : null,
        payment_option: paymentOption,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start booking.");
      setSubmitting(false);
    }
  };

  const applyVoucher = async () => {
    if (!voucherCode.trim()) return;
    setCheckingVoucher(true);
    try {
      const { data } = await api.post("/vouchers/check", { code: voucherCode.trim() });
      setVoucher(data);
      toast.success(`Voucher applied — $${(data.amount_cents / 100).toFixed(0)} off.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Voucher invalid.");
      setVoucher(null);
    } finally { setCheckingVoucher(false); }
  };

  return (
    <div data-testid="book-flow-page" className="max-w-5xl mx-auto px-6 sm:px-10 py-16">
      <div className="eyebrow-lg text-center">Book Your Session</div>
      <h1 className="mt-8 h-editorial text-5xl sm:text-6xl text-center">Choose <span className="italic-serif text-[#D4AF37]">where and when.</span></h1>
      <p className="mt-4 text-center text-sm text-zinc-500 font-mono-eyebrow uppercase tracking-[0.28em]">Experience · City · Time · Consent · Payment</p>

      {/* Step 1: City */}
      <section className="mt-16">
        <div className="eyebrow-lg text-center">01 · City</div>
        {loadingCities ? (
          <div data-testid="booking-loading" className="mt-8 text-center text-sm text-zinc-500 font-mono-eyebrow uppercase tracking-[0.24em]">Loading cities…</div>
        ) : citiesError ? (
          <div data-testid="booking-error" className="mt-8 hairline p-8 text-center text-sm text-red-300">{citiesError}</div>
        ) : bookableCities.length === 0 ? (
          <div data-testid="booking-empty-state" className="mt-8 hairline p-10 text-center">
            <p className="h-editorial italic-serif text-2xl sm:text-3xl text-zinc-300">No cities are open for booking yet.</p>
            <p className="mt-6 text-sm text-zinc-500 max-w-md mx-auto">Upcoming dates are listed on the Cities page. Request availability there and I&apos;ll reach out privately when hours open.</p>
            <Link to="/cities" data-testid="booking-empty-cities-link" className="mt-8 rc-btn-gold inline-flex items-center gap-2 min-h-[44px]">See Cities <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {bookableCities.map(c => (
              <button key={c.city_id} data-testid={`select-city-${c.city_id}`} onClick={() => { setCityId(c.city_id); setSlot(""); }}
                className={`hairline p-5 text-left transition-all relative min-h-[44px] ${cityId === c.city_id ? "border-[#00D1FF] glow-blue" : "hover:border-white/25"}`}>
                <MapPin className="w-4 h-4 text-[#00D1FF]" />
                <div className="mt-4 font-serif-editorial text-2xl">{c.name}</div>
                <div className="text-xs text-zinc-500 mt-1">{c.dates}</div>
                {c.timezone && <div className="text-[10px] text-zinc-600 mt-1 font-mono-eyebrow uppercase tracking-[0.22em]">{c.timezone.replace("_", " ")}</div>}
                {c.first_access_active && (
                  <div className="mt-3 text-[10px] font-mono-eyebrow uppercase tracking-[0.24em] text-[#D4AF37]">Rebel List First Access</div>
                )}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Step 2: Slot */}
      {activeCity && futureSlots.length > 0 && (
        <section className="mt-12">
          <div className="eyebrow eyebrow-blue">02 · Time{activeCity.timezone ? ` · ${activeCity.timezone.replace("_", " ")}` : ""}</div>
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {futureSlots.map(s => {
              const taken = (activeCity.booked_slots || []).includes(s);
              return (
                <button key={s} data-testid={`select-slot-${s}`} disabled={taken} onClick={() => !taken && setSlot(s)}
                  className={`hairline p-4 text-sm transition-all relative min-h-[44px] ${slot === s ? "border-[#00D1FF] bg-[#0052FF]/10" : taken ? "opacity-30 cursor-not-allowed line-through" : "hover:border-white/25"}`}>
                  {new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", weekday: "short", hour: "numeric", minute: "2-digit" })}
                  {taken && <span className="block text-[9px] font-mono-eyebrow uppercase tracking-[0.2em] text-zinc-500 mt-1">Reserved</span>}
                </button>
              );
            })}
          </div>
          {activeCity.booked_slots?.length > 0 && (
            <p className="mt-3 text-[11px] text-zinc-500 font-mono-eyebrow uppercase tracking-[0.18em]">Reserved slots hold for 20 minutes during checkout.</p>
          )}
        </section>
      )}
      {activeCity && futureSlots.length === 0 && (activeCity.slots || []).length > 0 && (
        <p data-testid="all-slots-past" className="mt-6 text-center text-sm text-zinc-500">All session times for {activeCity.name} have passed.</p>
      )}

      {/* Step 3: Package */}
      <section className="mt-12">
        <div className="eyebrow eyebrow-blue">03 · Package</div>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          {pkgs.map(p => (
            <button key={p.lookup_key} data-testid={`select-pkg-${p.lookup_key}`} onClick={() => setPkgKey(p.lookup_key)}
              className={`hairline p-6 text-left transition-all ${pkgKey === p.lookup_key ? "border-[#D4AF37] glow-gold" : "hover:border-white/25"}`}>
              <div className="eyebrow">{p.duration}</div>
              <div className="mt-3 font-serif-editorial text-2xl">{p.name}</div>
              <div className="mt-3 h-editorial text-3xl">${(p.amount/100).toLocaleString()}</div>
              <div className="text-xs text-zinc-500 mt-2">{p.tagline}</div>
            </button>
          ))}
        </div>
      </section>

      {/* Notes */}
      <section className="mt-12">
        <div className="eyebrow eyebrow-blue">04 · Anything to add?</div>
        <textarea data-testid="booking-notes" rows={4} value={notes} onChange={e => setNotes(e.target.value)} className="mt-5 w-full px-4 py-3" placeholder="Optional. Only I read this." />
      </section>

      {/* Voucher */}
      <section className="mt-12 hairline p-6">
        <div className="eyebrow eyebrow-blue flex items-center gap-2"><Gift className="w-3.5 h-3.5" /> Gift Voucher (optional)</div>
        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <input data-testid="voucher-input" value={voucherCode} onChange={e => setVoucherCode(e.target.value.toUpperCase())} placeholder="REBEL-XXXXXX" className="flex-1 px-4 py-3 font-mono-eyebrow tracking-widest" />
          {voucher ? (
            <button data-testid="voucher-clear" onClick={() => { setVoucher(null); setVoucherCode(""); }} className="rc-btn-ghost inline-flex items-center gap-2"><X className="w-3 h-3" /> Remove</button>
          ) : (
            <button data-testid="voucher-apply" onClick={applyVoucher} disabled={checkingVoucher || !voucherCode.trim()} className="rc-btn-gold">{checkingVoucher ? "Checking…" : "Apply"}</button>
          )}
        </div>
        {voucher && <p className="mt-3 text-xs text-[#00E699] font-mono-eyebrow uppercase tracking-[0.18em]">Voucher {voucher.code} — ${(voucher.amount_cents/100).toFixed(0)} off</p>}
      </section>

      {/* Deposit option (retreat only) */}
      {activePkg?.tier === "retreat" && (
        <section className="mt-12 hairline p-6" data-testid="deposit-section">
          <div className="eyebrow eyebrow-blue">Payment Plan</div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <button type="button" data-testid="payment-full" onClick={() => setPaymentOption("full")}
              className={`hairline p-5 text-left transition-all ${paymentOption === "full" ? "border-[#00D1FF] bg-[#0052FF]/10" : "hover:border-white/25"}`}>
              <div className="eyebrow">Pay in Full</div>
              <div className="mt-3 h-editorial text-3xl">${(activePkg.amount/100).toLocaleString()}</div>
              <div className="text-xs text-zinc-500 mt-1">One clean charge, nothing due later.</div>
            </button>
            <button type="button" data-testid="payment-deposit" onClick={() => setPaymentOption("deposit")}
              className={`hairline p-5 text-left transition-all ${paymentOption === "deposit" ? "border-[#D4AF37] glow-gold" : "hover:border-white/25"}`}>
              <div className="eyebrow">25% Deposit</div>
              <div className="mt-3 h-editorial text-3xl">${(activePkg.amount * 0.25/100).toLocaleString()}<span className="text-sm text-zinc-500"> now</span></div>
              <div className="text-xs text-zinc-500 mt-1">Balance ${(activePkg.amount * 0.75/100).toLocaleString()} due 14 days before first session.</div>
            </button>
          </div>
        </section>
      )}

      {/* Summary */}
      <section className="mt-12 hairline p-8">
        <div className="eyebrow">Summary</div>
        <div className="mt-4 grid sm:grid-cols-3 gap-6 text-sm">
          <div><div className="text-zinc-500 text-xs">City</div><div className="font-serif-editorial text-2xl mt-1">{activeCity?.name || "—"}</div></div>
          <div><div className="text-zinc-500 text-xs">Time</div><div className="text-lg mt-1">{slot ? new Date(slot).toLocaleString() : "—"}</div></div>
          <div>
            <div className="text-zinc-500 text-xs">Due today</div>
            {activePkg ? (() => {
              const isDep = paymentOption === "deposit" && activePkg.tier === "retreat";
              const listAmt = activePkg.amount;
              const dueAmt = isDep ? Math.max(Math.round(listAmt * 0.25) - (voucher?.amount_cents||0), 100) : Math.max(listAmt - (voucher?.amount_cents||0), 100);
              return (
                <div className="mt-1">
                  {voucher && <span className="text-zinc-500 line-through text-lg mr-2">${(listAmt/100).toLocaleString()}</span>}
                  <span className={`h-editorial text-3xl ${voucher || isDep ? "text-[#D4AF37]" : ""}`}>${(dueAmt/100).toLocaleString()}</span>
                  {isDep && <div className="text-xs text-zinc-500 mt-1">Balance ${((listAmt - Math.round(listAmt * 0.25))/100).toLocaleString()} later</div>}
                </div>
              );
            })() : <div className="h-editorial text-3xl mt-1">—</div>}
          </div>
        </div>
        <div className="mt-6 flex items-center gap-2 text-xs text-zinc-500"><CheckCircle2 className="w-4 h-4 text-[#00D1FF]" /> Booking arrives pending my approval. Full refund if declined.</div>
        <button data-testid="booking-submit" onClick={submit} disabled={submitting || !cityId || !slot || !pkgKey} className="mt-8 rc-btn-primary w-full disabled:opacity-40">
          {submitting ? "Redirecting to secure checkout…" : "Continue to Payment"}
        </button>
      </section>
    </div>
  );
}
